const STORAGE_KEY = 'bradley_floating_widget_position'
const EDGE_MARGIN_PX = 24
const HEADER_FALLBACK_HEIGHT_PX = 86
export const APP_HEADER_ID = 'app-header'
export const APP_SIDEBAR_ID = 'app-sidebar'
const VALID_EDGES = new Set(['top', 'right', 'bottom', 'left'])

function safeGetItem() {
  try {
    return localStorage.getItem(STORAGE_KEY)
  }
  catch {
    return null
  }
}

function safeSetItem(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  }
  catch {
  }
}

export function loadStoredPosition() {
  const raw = safeGetItem()
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw)
    if (!VALID_EDGES.has(parsed?.edge)) {
      return null
    }
    const offsetPx = Number(parsed.offsetPx)
    if (!Number.isFinite(offsetPx)) {
      return null
    }
    return { edge: parsed.edge, offsetPx }
  }
  catch {
    return null
  }
}

function getViewportSize() {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

export function getHeaderBottom() {
  const header = document.getElementById(APP_HEADER_ID)
  return header ? header.getBoundingClientRect().bottom : HEADER_FALLBACK_HEIGHT_PX
}

export function getSidebarRight() {
  const sidebar = document.getElementById(APP_SIDEBAR_ID)
  return sidebar ? sidebar.getBoundingClientRect().right : 0
}

export function getContentLeft() {
  const sidebarRight = getSidebarRight()
  return sidebarRight > 0 ? sidebarRight + EDGE_MARGIN_PX : EDGE_MARGIN_PX
}

export function getContentBounds(widgetWidth, widgetHeight) {
  const { width, height } = getViewportSize()
  const minTop = getHeaderBottom() + EDGE_MARGIN_PX
  const minLeft = getContentLeft()
  const maxLeft = Math.max(minLeft, width - widgetWidth - EDGE_MARGIN_PX)
  const maxTop = Math.max(minTop, height - widgetHeight - EDGE_MARGIN_PX)

  return { minTop, minLeft, maxLeft, maxTop }
}

function clampOffsetForEdge(edge, offsetPx, widgetWidth, widgetHeight) {
  const { minTop, minLeft, maxLeft, maxTop } = getContentBounds(widgetWidth, widgetHeight)

  if (edge === 'top' || edge === 'bottom') {
    return Math.min(maxLeft, Math.max(minLeft, offsetPx))
  }
  return Math.min(maxTop, Math.max(minTop, offsetPx))
}

function toLeftTopStyle(leftPx, topPx) {
  return {
    left: `${leftPx}px`,
    top: `${topPx}px`,
    right: 'auto',
    bottom: 'auto',
  }
}

export function positionToStyle(position, widgetWidth, widgetHeight) {
  const { width, height } = getViewportSize()
  const edge = position?.edge ?? 'bottom'
  const offsetPx = clampOffsetForEdge(
    edge,
    position?.offsetPx ?? EDGE_MARGIN_PX,
    widgetWidth,
    widgetHeight,
  )

  if (edge === 'bottom') {
    return toLeftTopStyle(offsetPx, height - widgetHeight - EDGE_MARGIN_PX)
  }
  if (edge === 'top') {
    return toLeftTopStyle(offsetPx, getHeaderBottom() + EDGE_MARGIN_PX)
  }
  if (edge === 'right') {
    return toLeftTopStyle(width - widgetWidth - EDGE_MARGIN_PX, offsetPx)
  }
  return toLeftTopStyle(getContentLeft(), offsetPx)
}

export function snapPositionFromRect(rect) {
  const { width: viewportWidth, height: viewportHeight } = getViewportSize()
  const widgetWidth = rect.width
  const widgetHeight = rect.height
  const contentTop = getHeaderBottom() + EDGE_MARGIN_PX
  const contentLeft = getContentLeft()

  const distances = [
    { edge: 'top', value: Math.abs(rect.top - contentTop) },
    { edge: 'right', value: Math.abs(viewportWidth - EDGE_MARGIN_PX - (rect.left + widgetWidth)) },
    { edge: 'bottom', value: Math.abs(viewportHeight - EDGE_MARGIN_PX - (rect.top + widgetHeight)) },
    { edge: 'left', value: Math.abs(rect.left - contentLeft) },
  ]

  distances.sort((a, b) => a.value - b.value)
  const edge = distances[0].edge

  const offsetPx = (edge === 'bottom' || edge === 'top') ? rect.left : rect.top

  return {
    edge,
    offsetPx: clampOffsetForEdge(edge, offsetPx, widgetWidth, widgetHeight),
  }
}

export function freePositionToStyle(leftPx, topPx, widgetWidth, widgetHeight) {
  const { minTop, minLeft, maxLeft, maxTop } = getContentBounds(widgetWidth, widgetHeight)

  return toLeftTopStyle(
    Math.min(maxLeft, Math.max(minLeft, leftPx)),
    Math.min(maxTop, Math.max(minTop, topPx)),
  )
}

export function clampSnappedPosition(position, widgetWidth, widgetHeight) {
  if (!position?.edge || widgetWidth <= 0 || widgetHeight <= 0) {
    return position
  }

  return {
    edge: position.edge,
    offsetPx: clampOffsetForEdge(
      position.edge,
      position.offsetPx,
      widgetWidth,
      widgetHeight,
    ),
  }
}

export function getDefaultBottomRightPosition(widgetWidth, widgetHeight) {
  const { width } = getViewportSize()
  return {
    edge: 'bottom',
    offsetPx: clampOffsetForEdge(
      'bottom',
      width - widgetWidth - EDGE_MARGIN_PX,
      widgetWidth,
      widgetHeight,
    ),
  }
}

function persistPosition(position) {
  if (position == null) {
    try {
      localStorage.removeItem(STORAGE_KEY)
    }
    catch {}
    return
  }
  safeSetItem(JSON.stringify(position))
}

export function useBradleyFloatingWidgetPosition() {
  return { defaultPosition: loadStoredPosition(), persistPosition }
}
